import { add, getMonth, sub } from 'date-fns'
import { DateRange } from 'react-day-picker'

export const getQuarterToDate = () => {
  const today = new Date()
  const currentMonth = getMonth(today)
  let from, to

  if (currentMonth < 3) {
    // Q1: January - March
    from = new Date(today.getFullYear(), 0, 1)
  } else if (currentMonth < 6) {
    // Q2: April - June
    from = new Date(today.getFullYear(), 3, 1)
  } else if (currentMonth < 9) {
    // Q3: July - September
    from = new Date(today.getFullYear(), 6, 1)
  } else {
    // Q4: October - December
    from = new Date(today.getFullYear(), 9, 1)
  }
  to = new Date(new Date().setDate(new Date().getDate() - 1))

  return {
    from,
    to,
  }
}

export const getQuarterlyRange = () => {
  const today = new Date()
  const currentMonth = getMonth(today)
  let from, to

  if (currentMonth < 3) {
    from = new Date(today.getFullYear() - 1, 9, 1)
    to = new Date(today.getFullYear() - 1, 11, 31)
  } else if (currentMonth < 6) {
    from = new Date(today.getFullYear(), 0, 1)
    to = new Date(today.getFullYear(), 2, 31)
  } else if (currentMonth < 9) {
    from = new Date(today.getFullYear(), 3, 1)
    to = new Date(today.getFullYear(), 5, 30)
  } else {
    from = new Date(today.getFullYear(), 6, 1)
    to = new Date(today.getFullYear(), 8, 30)
  }

  return {
    from,
    to,
  }
}

export const getCurrentQuarter = (date: Date) => {
  const currentMonth = date.getMonth()
  let quarter
  if (currentMonth < 3) {
    quarter = 1
  } else if (currentMonth < 6) {
    quarter = 2
  } else if (currentMonth < 9) {
    quarter = 3
  } else {
    quarter = 4
  }
  return quarter
}

export const getQuarterStart = (
  quarter: number,
  year: number = new Date().getFullYear()
) => {
  switch (quarter) {
    case 1:
      return new Date(year, 0, 1)
    case 2:
      return new Date(year, 3, 1)
    case 3:
      return new Date(year, 6, 1)
    case 4:
      return new Date(year, 9, 1)
    default:
      return new Date()
  }
}

export const getQuarterEnd = (
  quarter: number,
  year: number = new Date().getFullYear()
) => {
  switch (quarter) {
    case 1:
      return new Date(year, 2, 31)
    case 2:
      return new Date(year, 5, 30)
    case 3:
      return new Date(year, 8, 30)
    case 4:
      return new Date(year, 11, 31)
    default:
      return new Date()
  }
}

export const getWeekToDate = () => {
  const today = sub(new Date(), { days: 1 })

  const monday = new Date(today)
  monday.setDate(monday.getDate() - today.getDay() + 1) // Set to Monday
  monday.setHours(0, 0, 0, 0) // Start of day

  return {
    from: monday,
    to: today,
  }
}

export type DateType =
  | 'daily'
  | 'weekly'
  | 'week-to-date'
  | 'monthly'
  | 'month-to-date'
  | 'quarterly'
  | 'quarter-to-date'
  | 'yearly'
  | 'year-to-date'
  | 'custom'
  | 'test-range'

/**
 * Returns the date for trending calculation for a given date range
 * Trending calculation is based on the difference between the start and end date
 *
 * @param startDate
 * @param endDate
 */
export const getTrendingDate = (
  dateRange: { from: Date; to: Date },
  type: DateType
) => {
  const { from: startDate, to: endDate } = dateRange

  const diffInDays = Math.floor(
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
  )
  const startingDay = startDate.getDay()
  switch (type) {
    case 'daily':
      // get the previous day range
      return {
        from: sub(startDate, { days: 1 }),
        to: sub(endDate, { days: 1 }),
      } as DateRange
    case 'weekly':
    case 'week-to-date':
      // get the previous week range
      return {
        from: sub(startDate, { days: 7 }),
        to: sub(endDate, { days: 7 }),
      } as DateRange
    case 'month-to-date':
      // find the weekday same as the startingDay of the previous month
      // for ex. if the startingDay is 3 (Wednesday), find the first Wednesday of the previous month
      const prevMonth = sub(startDate, { months: 1 })
      let dayDiff = startingDay - prevMonth.getDay() + 1
      if (dayDiff < 0) {
        dayDiff = dayDiff + 7
      }
      const prevMonthWeekday = new Date(prevMonth.setDate(dayDiff))
      return {
        from: prevMonthWeekday,
        to: add(prevMonthWeekday, { days: diffInDays }),
      } as DateRange
    case 'monthly':
      const prevMonthStart = new Date(
        startDate.getFullYear(),
        startDate.getMonth() - 1,
        1
      )
      const prevMonthEnd = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        0
      )
      return {
        from: prevMonthStart,
        to: prevMonthEnd,
      } as DateRange
    case 'quarterly':
      return {
        from: new Date(startDate.getFullYear(), startDate.getMonth() - 3, 1),
        to: new Date(startDate.getFullYear(), startDate.getMonth(), 0),
      } as DateRange
    case 'quarter-to-date':
      // find the weekday same as the startingDay of the previous quarter
      const prevQuarterStart = new Date(
        startDate.getFullYear(),
        startDate.getMonth() - 3,
        1
      )
      return {
        from: prevQuarterStart,
        to: add(prevQuarterStart, { days: diffInDays }),
      } as DateRange
    case 'yearly':
      return {
        from: new Date(startDate.getFullYear() - 1, 0, 1),
        to: new Date(startDate.getFullYear() - 1, 12, 31),
      } as DateRange
    case 'year-to-date':
      const prevYear = sub(startDate, { years: 1 })
      return {
        from: new Date(prevYear.getFullYear(), startDate.getMonth(), 1),
        to: add(prevYear, { days: diffInDays }),
      } as DateRange
    case 'custom':
    default:
      const customDateEnd = sub(startDate, { days: 1 })
      return {
        from: sub(customDateEnd, { days: diffInDays }),
        to: customDateEnd,
      } as DateRange
  }
}

/**
 * Returns the date range for a given type
 * -ly suffix indicates the previous date range -> weekly for example should return the previous whole week + the week before that in trending
 * -to-date suffix indicates the current date range -> week-to-date for example should return the current week up to yesterday
 */
export const getDateRange = (type: DateType, date?: Date) => {
  const today = date || sub(new Date(), { days: 1 })

  switch (type) {
    case 'daily':
      const currentDaily = {
        from: sub(new Date(today), { days: 1 }),
        to: new Date(today),
      }
      return {
        ...currentDaily,
        trending: getTrendingDate(currentDaily, 'daily'),
      }
    case 'weekly':
      const weekDay = today.getDay()
      const weeklyTo = new Date(today.setDate(today.getDate() - weekDay))
      const currentWeek = {
        from: sub(weeklyTo, { days: 6 }),
        to: weeklyTo,
      }
      return {
        ...currentWeek,
        trending: getTrendingDate(currentWeek, 'weekly'),
      }
    case 'week-to-date':
      const weekToDate = getWeekToDate()
      return {
        ...weekToDate,
        trending: getTrendingDate(weekToDate, 'week-to-date'),
      }
    case 'monthly':
      const monthStartDate = new Date(today.getFullYear(), today.getMonth(), 1)
      const currentMonth = {
        from: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        to: sub(monthStartDate, { days: 1 }),
      }
      return {
        ...currentMonth,
        trending: getTrendingDate(currentMonth, 'monthly'),
      }
    case 'month-to-date':
      const monthToDate = {
        from: new Date(today.getFullYear(), today.getMonth(), 1),
        to: today,
      }
      return {
        ...monthToDate,
        trending: getTrendingDate(monthToDate, 'month-to-date'),
      }
    case 'quarterly':
      const prevQ = getQuarterlyRange()
      return {
        ...prevQ,
        trending: getTrendingDate(prevQ, 'quarterly'),
      }
    case 'quarter-to-date':
      const quarterToDate = getQuarterToDate()
      return {
        ...quarterToDate,
        trending: getTrendingDate(quarterToDate, 'quarter-to-date'),
      }
    case 'yearly':
      const currentYear = {
        from: new Date(today.getFullYear() - 1, 0, 1),
        to: new Date(today.getFullYear() - 1, 11, 31),
      }
      return {
        ...currentYear,
        trending: getTrendingDate(currentYear, 'yearly'),
      }
    case 'year-to-date':
      const yearToDate = {
        from: new Date(today.getFullYear(), 0, 1),
        to: today,
      }
      return {
        ...yearToDate,
        trending: getTrendingDate(yearToDate, 'year-to-date'),
      }
    default:
      return {
        from: new Date(),
        to: new Date(),
        trending: getTrendingDate(
          { from: new Date(), to: new Date() },
          'custom'
        ),
      }
  }
}

export const getAllRangesForDate = () => {
  const ALL_RANGES = [
    'daily',
    'weekly',
    'week-to-date',
    'monthly',
    'month-to-date',
    'quarterly',
    'quarter-to-date',
    'yearly',
    'year-to-date',
  ]

  return ALL_RANGES.map((type) => {
    const range = getDateRange(type as keyof typeof getDateRange)
    return {
      ...range,
      type,
    }
  })
}

export const convertValueToLabel = (value: string) => {
  return value
    .split('-')
    .map((word) => {
      if (word === 'to') return word
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}
